<script setup lang="ts">
import { isHttpUrl } from '~/utils/isHttpUrl';

const { invite, isDialogVisible, respond, dismiss, close } = useInterviewInviteStore();
const { user } = useUserStore();
const { dispatchMessage } = useSystemMessages();
const { t } = useI18n();

const firstName = computed(() => user.value?.firstName?.trim() ?? '');
const title = computed(() => (firstName.value
  ? t('interviewInvite.title', { name: firstName.value })
  : t('interviewInvite.titleNoName')));

const showSchedulerError = () => {
  dispatchMessage(t('interviewInvite.errors.title'), 'error', t('interviewInvite.errors.schedulerUnavailable'));
};

const onNotNow = () => {
  if (!isDialogVisible.value) {
    return;
  }
  dismiss();
};

const onBookCall = () => {
  if (!isDialogVisible.value) {
    return;
  }

  const schedulerUrl = invite.value?.schedulerUrl;
  if (!isHttpUrl(schedulerUrl)) {
    useNuxtApp().$sentry?.captureException(new Error(`Interview invite scheduler URL is invalid: ${schedulerUrl}`));
    showSchedulerError();
    return;
  }

  const schedulerWindow = window.open(schedulerUrl, '_blank');
  if (!schedulerWindow) {
    showSchedulerError();
    return;
  }
  schedulerWindow.opener = null;

  respond();
};
</script>

<template>
  <Dialog
    v-if="invite"
    :visible="isDialogVisible"
    :draggable="false"
    :closable="false"
    dismissable-mask
    modal
    position="bottom"
    :show-header="false"
    :aria-label="title"
    class="w-full! lg:w-auto! lg:max-w-[436px]! rounded-t-2xl! rounded-b-none! lg:rounded-2xl! h-auto! max-h-[90dvh]! lg:max-h-[85dvh]! m-0!"
    :pt="{
      mask: 'items-end! lg:items-center!',
      transition: {
        enterFromClass: 'translate-y-full',
        enterActiveClass: 'transition-transform duration-300 ease-out',
        leaveToClass: 'translate-y-full',
        leaveActiveClass: 'transition-transform duration-300 ease-in'
      },
      content: 'p-0!'
    }"
    @update:visible="close()"
  >
    <div class="w-full p-8 flex flex-col gap-8">
      <div class="flex flex-col gap-6">
        <div class="flex flex-col items-center gap-2">
          <img
            src="~/assets/images/gifs/interview-invite.gif"
            alt=""
            width="160"
            height="160"
            class="w-40 h-40 object-contain"
          >
          <div class="text-24 font-bold text-center text-neutral-90">
            {{title}}
          </div>
        </div>

        <p class="text-16 leading-relaxed text-center text-neutral-90">
          {{t('interviewInvite.body')}}
        </p>

        <p class="text-16 font-semibold text-center text-neutral-80">
          {{t('interviewInvite.meta')}}
        </p>
      </div>

      <div class="flex gap-4">
        <Button
          data-testid="not-now"
          class="flex-1"
          severity="secondary"
          outlined
          autofocus
          :label="t('interviewInvite.notNow')"
          @click="onNotNow"
        />
        <Button
          data-testid="book-call"
          class="flex-1"
          :label="t('interviewInvite.bookCall')"
          @click="onBookCall"
        />
      </div>
    </div>
  </Dialog>
</template>
